/**
 * Phase 16 Plan 16-03 — CompleteWorkOrderDialog
 *
 * Confirms completion of an Active Work Order (Active → Completed).
 * On 200 with non-empty `warnings` array, renders warnings as a yellow alert
 * before closing — user sees the warnings and can acknowledge.
 */

import { useState, type JSX } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCompleteWorkOrderMutation } from '@/hooks/useWorkOrderQueries';
import { toast } from '@/lib/toast';
import type { WorkOrderDetail } from '@/types/work-orders';

export interface CompleteWorkOrderDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (o: boolean) => void;
  readonly workOrder: WorkOrderDetail;
}

export function CompleteWorkOrderDialog({
  open,
  onOpenChange,
  workOrder,
}: CompleteWorkOrderDialogProps): JSX.Element {
  const [warnings, setWarnings] = useState<readonly string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [completed, setCompleted] = useState<boolean>(false);
  const mutation = useCompleteWorkOrderMutation();

  function handleClose(): void {
    setWarnings([]);
    setErrorMessage(null);
    setCompleted(false);
    mutation.reset();
    onOpenChange(false);
  }

  function handleConfirm(): void {
    setErrorMessage(null);
    mutation.mutate(workOrder.id, {
      onSuccess: (result) => {
        if (result.warnings.length > 0) {
          setWarnings(result.warnings);
          setCompleted(true);
          toast.success(`Work Order ${workOrder.woNumber} completed (with warnings)`);
        } else {
          toast.success(`Work Order ${workOrder.woNumber} completed`);
          onOpenChange(false);
        }
      },
      onError: (err) => {
        const msg = err.detail ?? err.message;
        setErrorMessage(msg);
        toast.error('Complete failed', { description: msg });
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent data-testid="complete-wo-dialog">
        <DialogHeader>
          <DialogTitle>Complete Work Order</DialogTitle>
          <DialogDescription>
            Mark <strong>{workOrder.woNumber}</strong> as Completed.
            Work Orders can be reopened later if needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          {warnings.length > 0 && (
            <div
              className="rounded-md border border-yellow-400 bg-yellow-50 px-3 py-2 text-sm text-yellow-800"
              data-testid="complete-wo-dialog-warnings"
            >
              <p className="font-medium mb-1">Completed with warnings:</p>
              <ul className="list-disc pl-5 space-y-1">
                {warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            </div>
          )}
          {errorMessage !== null && (
            <div
              className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm text-destructive"
              data-testid="complete-wo-dialog-error"
            >
              {errorMessage}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
            data-testid="complete-wo-dialog-cancel"
          >
            {completed ? 'Close' : 'Cancel'}
          </Button>
          {!completed && (
            <Button
              type="button"
              onClick={handleConfirm}
              disabled={mutation.isPending}
              data-testid="complete-wo-dialog-confirm"
            >
              {mutation.isPending ? 'Completing…' : 'Complete'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
